import React, { FC } from 'react';
import moment from 'moment-timezone';
import { Popover } from 'antd';
import { Link } from 'react-router-dom';
import DateTimeHover from '../date_time_component/DateTime';
import useElementSize from '../../hooks/useElementSize';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getWalkthroughHeatMapColorsForPercentage } from '../../utils/WalkthroughHeatMapRangeUtils';
import { WalkthroughHeatMapRange } from '../../types/walkthrough_heat_map_ranges';
import { HeatmapCell, HeatmapMonth } from './heatmapUtils';
import {
	FIXED_CELL_PX,
	getColumnWeekCounts,
	getFittingMonthCount,
	getFittingMonthsPerRow,
	MIN_MONTH_SPACER_PX,
} from './heatmapLayout';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// The whole calendar is one CSS grid: a weekday-label column, then per month column a 4px spacer
// track and one track per week. Grid rows are shared by the labels and the cells, so a label always
// sits level with its row, and months in the same column line up from one calendar row to the next.
// With the grid's 4px gap, the spacer puts the first month 12px from the labels. By default the
// spacers between months also soak up any leftover width, so the months spread across the container.
const FIRST_MONTH_SPACER_TRACK = '4px';
const MONTH_SPACER_TRACK = `minmax(${MIN_MONTH_SPACER_PX}px, 1fr)`;
// A month header plus its seven day rows.
const GRID_ROWS_PER_CALENDAR_ROW = 8;

const formatScorePercentage = (percentage: number | null) =>
	percentage === null ? null : `${percentage.toFixed(1)}%`;

interface CellPopoverContentProps {
	cell: HeatmapCell;
	userType: string;
	timeZoneId?: string;
}

const CellPopoverContent: FC<CellPopoverContentProps> = ({ cell, userType, timeZoneId }) => (
	<div className="bodyregular space-y-2 text-character-primary">
		{cell.percentageScore !== null ? (
			<div>
				<span className="bodymedium">
					{cell.walkthroughs.length > 1 ? 'Average Score' : 'Score'}:
				</span>{' '}
				{formatScorePercentage(cell.percentageScore)}
			</div>
		) : (
			<div>No score available</div>
		)}
		{/* Shrinks on short viewports; 200px leaves room for the popover title, score row, and arrow. */}
		<div className="max-h-[min(426px,calc(100vh-200px))] space-y-2 overflow-y-auto">
			{cell.walkthroughs.map((walkthrough, index) => (
				<div
					key={walkthrough.id}
					className="border-0 border-b border-solid border-grey-4 pb-2 last:border-b-0"
				>
					<Link
						to={`/${userType}/siteSurveys/walkthrough/detail/${walkthrough.id}/details`}
						className="flex flex-col text-blue-600 hover:underline"
					>
						<span className="bodymedium">
							{index + 1}. {walkthrough.title || walkthrough.name || 'Walkthrough'}
						</span>
						<span className="footnoteregular text-character-secondary">
							<DateTimeHover
								timestamp={walkthrough.completedAt}
								dateFormat={'MMM Do, h:mm A z'}
								showDate
								timeZone={timeZoneId}
							/>
							<br />
							{walkthrough.percentageScore !== null ? (
								<>
									Score:{' '}
									<span className="footnoteMedium">
										{formatScorePercentage(walkthrough.percentageScore)}
									</span>
								</>
							) : null}
						</span>
					</Link>
				</div>
			))}
		</div>
	</div>
);

interface DayCellProps {
	cell: HeatmapCell;
	gridPlacement: React.CSSProperties;
	heatMapRanges: WalkthroughHeatMapRange[];
	userType: string;
	timeZoneId?: string;
}

const DayCell: FC<DayCellProps> = ({
	cell,
	gridPlacement,
	heatMapRanges,
	userType,
	timeZoneId,
}) => {
	const colors = getWalkthroughHeatMapColorsForPercentage(cell.percentageScore, heatMapRanges);
	// Only a day with walkthroughs has anything to show on hover — even unscored ones, whose links
	// live in the popover. Empty and future days stay inert.
	const hasWalkthroughs = cell.walkthroughs.length > 0;
	const dayNumber = (
		<div
			className={`bodyregular flex w-full items-center justify-center rounded ${
				cell.isFuture ? 'opacity-40' : ''
			} ${hasWalkthroughs ? 'cursor-pointer' : ''} ${
				cell.isToday ? 'outline outline-2 outline-offset-1 outline-grey-9' : ''
			}`}
			// Square at whatever width the week track gives it. Inline because this repo's
			// @tailwindcss/aspect-ratio plugin disables Tailwind's own aspect-square utility.
			style={{
				...gridPlacement,
				aspectRatio: '1 / 1',
				backgroundColor: colors.swatch,
				color: colors.swatchText,
			}}
		>
			{cell.dayOfMonth}
		</div>
	);

	if (!hasWalkthroughs) {
		return dayNumber;
	}

	return (
		<Popover
			title={moment(cell.date).format('MMM D, YYYY')}
			content={<CellPopoverContent cell={cell} userType={userType} timeZoneId={timeZoneId} />}
		>
			{dayNumber}
		</Popover>
	);
};

interface WalkthroughHeatmapCalendarProps {
	months: HeatmapMonth[];
	// Months are laid out left to right, wrapping onto a new calendar row after this many. Every
	// month column is as wide as its widest month, so months line up vertically across rows.
	monthsPerRow: number;
	// Show only the most recent months that fit the container's width on one row at fixed cells (the
	// card), so the current month always sits at the right edge. Ignores monthsPerRow.
	fitMonthsToWidth?: boolean;
	// Width of each square day cell. Defaults to the card's 28px.
	cellPx?: number;
	// A fixed spacer between months, in px, instead of the stretchy default.
	monthSpacerPx?: number;
	// Wrap after fewer months than monthsPerRow when a row that long would be wider than this many
	// px: the most months per row that fit and divide the months evenly (the expanded view).
	wrapToWidthPx?: number;
	heatMapRanges: WalkthroughHeatMapRange[];
	userType: string;
	timeZoneId?: string;
}

export const WalkthroughHeatmapCalendar: FC<WalkthroughHeatmapCalendarProps> = ({
	months: allMonths,
	monthsPerRow: requestedMonthsPerRow,
	fitMonthsToWidth,
	cellPx = FIXED_CELL_PX,
	monthSpacerPx,
	wrapToWidthPx,
	heatMapRanges,
	userType,
	timeZoneId,
}) => {
	// Measured on mount before the browser paints, so the first frame already shows the fitted months.
	const [calendarRef, { width: calendarWidth }] = useElementSize();
	const months = fitMonthsToWidth
		? allMonths.slice(-getFittingMonthCount(allMonths, calendarWidth))
		: allMonths;
	let monthsPerRow = requestedMonthsPerRow;
	if (fitMonthsToWidth) {
		monthsPerRow = months.length;
	} else if (wrapToWidthPx) {
		monthsPerRow = getFittingMonthsPerRow(
			months,
			requestedMonthsPerRow,
			wrapToWidthPx,
			cellPx,
			monthSpacerPx || MIN_MONTH_SPACER_PX
		);
	}

	const calendarRowCount = Math.ceil(months.length / monthsPerRow);
	const columnWeekCounts = getColumnWeekCounts(months, monthsPerRow);
	// Grid line where each month column's first week starts (line 1 is the label column, then each
	// month column is its spacer track followed by its week tracks).
	const columnFirstWeekLines = columnWeekCounts.map(
		(unused, columnIndex) =>
			3 +
			columnWeekCounts
				.slice(0, columnIndex)
				.reduce((lineCount, weekCount) => lineCount + 1 + weekCount, 0)
	);
	const weekTrack = `${cellPx}px`;
	const monthSpacerTrack = monthSpacerPx ? `${monthSpacerPx}px` : MONTH_SPACER_TRACK;
	const gridTemplateColumns = [
		'auto',
		...columnWeekCounts.map(
			(weekCount, columnIndex) =>
				`${
					columnIndex === 0 ? FIRST_MONTH_SPACER_TRACK : monthSpacerTrack
				} repeat(${weekCount}, ${weekTrack})`
		),
	].join(' ');
	const currentMonthClasses =
		userType === ROLE_TYPES.SUPPLIER ? 'bg-cyan-1 border-cyan-7' : 'bg-purple-1 border-purple-6';

	return (
		// Scrolls sideways rather than squeezing the cells when the container is narrower than the
		// months; pr-1/pb-1 leave room for today's outline on the right and bottom edges.
		<div ref={calendarRef} className="overflow-x-auto pb-1 pr-1">
			{/* justify-end keeps a lone month (no stretchy spacers) against the right edge too */}
			<div className="grid justify-end gap-1" style={{ gridTemplateColumns }}>
				{Array.from({ length: calendarRowCount }, (unused, calendarRowIndex) => {
					const headerRow = calendarRowIndex * GRID_ROWS_PER_CALENDAR_ROW + 1;
					return DAY_LABELS.map((day, dayIndex) => (
						<div
							key={`${calendarRowIndex}-${day}`}
							className="footnoteregular flex w-8 items-center text-character-secondary"
							style={{ gridColumn: 1, gridRow: headerRow + 1 + dayIndex }}
						>
							{day}
						</div>
					));
				})}
				{months.map((month, monthIndex) => {
					const calendarRowIndex = Math.floor(monthIndex / monthsPerRow);
					const columnIndex = monthIndex % monthsPerRow;
					const headerRow = calendarRowIndex * GRID_ROWS_PER_CALENDAR_ROW + 1;
					const firstWeekLine = columnFirstWeekLines[columnIndex];
					return (
						<React.Fragment key={month.key}>
							<div
								// mb-1 + the 4px row gap puts the cells 8px below the header; calendar rows
								// after the first get 24px above their headers (mt-5 + the row gap).
								className={`mb-1 flex h-[52px] flex-col justify-center rounded-t border-0 border-b-2 border-solid px-2 ${
									calendarRowIndex > 0 ? 'mt-5' : ''
								} ${month.isCurrentMonth ? currentMonthClasses : 'border-grey-5'}`}
								// Spans the whole column, so header rules match across calendar rows.
								style={{
									gridRow: headerRow,
									gridColumn: `${firstWeekLine} / span ${columnWeekCounts[columnIndex]}`,
								}}
							>
								<span className="bodymedium text-character-primary">{month.monthLabel}</span>
								<span className="footnoteregular text-character-secondary">{month.yearLabel}</span>
							</div>
							{month.weeks.map((week, weekIndex) =>
								week.map((cell, dayIndex) =>
									cell ? (
										<DayCell
											key={cell.date}
											cell={cell}
											gridPlacement={{
												gridRow: headerRow + 1 + dayIndex,
												gridColumn: firstWeekLine + weekIndex,
											}}
											heatMapRanges={heatMapRanges}
											userType={userType}
											timeZoneId={timeZoneId}
										/>
									) : null
								)
							)}
						</React.Fragment>
					);
				})}
			</div>
		</div>
	);
};
